import pptxgen from "pptxgenjs";

/**
 * Convert image URL -> base64
 */
const getBase64FromUrl = async (url) => {
  if (!url) return null;

  try {
    const bust = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;

    const res = await fetch(bust, {
      mode: "cors",
      cache: "no-cache",
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn("Image load failed:", url, err);
    return null;
  }
};

/**
 * Get image dimensions from base64
 */
const getImageDimensions = (base64) => {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };

    img.onerror = () => {
      resolve({
        width: 1,
        height: 1,
      });
    };

    img.src = base64;
  });
};

/**
 * Fit image inside container WITHOUT stretching
 */
const calculateContainFit = async (
  base64,
  containerX,
  containerY,
  containerW,
  containerH,
) => {
  const { width, height } = await getImageDimensions(base64);

  const imageRatio = width / height;
  const containerRatio = containerW / containerH;

  let finalW;
  let finalH;
  let finalX;
  let finalY;

  if (imageRatio > containerRatio) {
    // Wider image
    finalW = containerW;
    finalH = containerW / imageRatio;

    finalX = containerX;
    finalY = containerY + (containerH - finalH) / 2;
  } else {
    // Taller image
    finalH = containerH;
    finalW = containerH * imageRatio;

    finalX = containerX + (containerW - finalW) / 2;
    finalY = containerY;
  }

  return {
    x: finalX,
    y: finalY,
    w: finalW,
    h: finalH,
  };
};

// Preload logo
const pixellLogoBase64 = await getBase64FromUrl(
  "/assests/images/pixell dark.png",
);

export default async function generatePaintingPPT({
  CampaignDetails,
  jobs = [],
}) {
  const pres = new pptxgen();

  pres.layout = "LAYOUT_16x9";

  const campaignTitle =
    CampaignDetails?.data?.campaignTitle || "Painting Report";

  pres.title = campaignTitle;

  // COLORS
  const C = {
    white: "FFFFFF",
    bgLight: "FAFAFA",
    skyCore: "0EA5E9",
    skyLight: "F0F9FF",
    skyBorder: "E0F2FE",
    slateNavy: "0F172A",
    muted: "64748B",
    border: "E2E8F0",
    gray: "F1F5F9",
  };

  const SW = 10;
  const SH = 5.625;

  const TOP_H = 1.0;
  const META_H = 0.7;
  const BOT_H = 1.0;

  const PAD = 0.2;

  // LOOP JOBS
  for (let i = 0; i < jobs.length; i++) {
    const jobData = jobs[i];

    const painting = jobData?.paintingJobRelatedFields || {};

    const storeName = jobData?.storeName || "Unassigned";

    const productType = jobData?.productId?.productType || "N/A";

    const paintedWidth = jobData?.productId?.widthOfPaintedProduct || "N/A";

    const paintedHeight = jobData?.productId?.heightOfPaintedProduct || "N/A";

    const paintedArea = painting?.totalPaintedArea
      ? `${paintedWidth} X ${paintedHeight} = ${painting.totalPaintedArea} SQ.FT`
      : "N/A";

    const address =
      [jobData?.address, jobData?.city, jobData?.state]
        .filter(Boolean)
        .join(", ") || "No address logged";

    const videoLink = painting?.paintVideo || null;

    const documentLink = painting?.signedDocument || null;

    const rawImages = (painting?.paintingImages || []).slice(0, 2);

    // Load images
    const images = await Promise.all(
      rawImages.map(async (url) => ({
        url,
        b64: await getBase64FromUrl(url),
      })),
    );

    // CREATE SLIDE
    const s = pres.addSlide();

    s.background = {
      color: C.bgLight,
    };

    /**
     * HEADER
     */
    s.addShape(pres.shapes.RECTANGLE, {
      x: 0,
      y: 0,
      w: SW,
      h: TOP_H,
      fill: { color: C.white },
      line: {
        color: C.border,
        width: 1,
      },
    });

    s.addShape(pres.shapes.RECTANGLE, {
      x: 0.2,
      y: 0.3,
      w: 0.04,
      h: 0.4,
      fill: {
        color: C.skyCore,
      },
    });

    s.addText("CAMPAIGN BRANDING PREVIEW", {
      x: 0.35,
      y: 0.25,
      w: 4,
      h: 0.2,
      fontSize: 9,
      color: C.skyCore,
      bold: true,
      charSpacing: 1.2,
    });

    s.addText(campaignTitle, {
      x: 0.35,
      y: 0.45,
      w: 6,
      h: 0.35,
      fontSize: 18,
      color: C.slateNavy,
      bold: true,
    });

    /**
     * LOGO
     */
    if (pixellLogoBase64) {
      const logoFit = await calculateContainFit(
        pixellLogoBase64,
        SW - 2.2,
        0.25,
        1.8,
        0.5,
      );

      s.addImage({
        data: pixellLogoBase64,
        ...logoFit,
      });
    }

    /**
     * META SECTION
     */
    const metaY = TOP_H;

    s.addShape(pres.shapes.RECTANGLE, {
      x: 0,
      y: metaY,
      w: SW,
      h: META_H,
      fill: {
        color: C.white,
      },
      line: {
        color: C.border,
        width: 1,
      },
    });

    const colW = SW / 3;

    const labels = [
      "STORE ENTITY",
      "PRODUCT GROUP TYPE",
      "AUDITED PAINTING FOOTPRINT",
    ];

    const values = [storeName, productType, paintedArea];

    labels.forEach((label, idx) => {
      const xPos = idx * colW;

      s.addText(label, {
        x: xPos + 0.3,
        y: metaY + 0.15,
        w: colW - 0.6,
        h: 0.2,
        fontSize: 8,
        color: C.muted,
        bold: true,
      });

      s.addText(values[idx], {
        x: xPos + 0.3,
        y: metaY + 0.35,
        w: colW - 0.6,
        h: 0.25,
        fontSize: 12,
        color: idx === 2 ? C.skyCore : C.slateNavy,
        bold: true,
        fit: "shrink",
      });

      if (idx < 2) {
        s.addShape(pres.shapes.LINE, {
          x: xPos + colW,
          y: metaY + 0.15,
          w: 0,
          h: 0.4,
          line: {
            color: C.border,
            width: 1,
          },
        });
      }
    });

    /**
     * ADDRESS
     */
    const addrY = metaY + META_H;

    s.addText(address, {
      x: 0.3,
      y: addrY,
      w: SW - 0.6,
      h: 0.4,
      fontSize: 9,
      color: C.slateNavy,
      margin: 10,
    });

    /**
     * IMAGES
     */
    const imgY = addrY + 0.45;

    const imgW = (SW - PAD * 3) / 2;

    const imgH = 2.3;

    for (let idx = 0; idx < 2; idx++) {
      const xPos = PAD + idx * (imgW + PAD);

      const b64 = images[idx]?.b64;

      // Card
      s.addShape(pres.shapes.RECTANGLE, {
        x: xPos,
        y: imgY,
        w: imgW,
        h: imgH,
        fill: {
          color: C.white,
        },
        line: {
          color: C.border,
          width: 1,
        },
        rectRadius: 0.05,
      });

      if (b64) {
        const fittedImage = await calculateContainFit(
          b64,
          xPos + 0.05,
          imgY + 0.05,
          imgW - 0.1,
          imgH - 0.1,
        );

        s.addImage({
          data: b64,
          ...fittedImage,
        });
      } else {
        s.addText("No Proof Photo Provided", {
          x: xPos,
          y: imgY,
          w: imgW,
          h: imgH,
          fontSize: 10,
          color: C.muted,
          align: "center",
          valign: "mid",
          italic: true,
        });
      }
    }

    /**
     * FOOTER
     */
    const footerY = SH - BOT_H - 0.1;

    const footW = (SW - PAD * 3) / 2;

    const footers = [
      {
        label: "COMPLIANCE RECORDING (VIDEO)",
        val: videoLink,
        accent: C.slateNavy,
      },
      {
        label: "AUTHORIZED HANDOVER DOCUMENTATION",
        val: documentLink,
        accent: C.skyCore,
      },
    ];

    footers.forEach((f, idx) => {
      const xPos = PAD + idx * (footW + PAD);

      s.addShape(pres.shapes.RECTANGLE, {
        x: xPos,
        y: footerY,
        w: footW,
        h: BOT_H,
        fill: {
          color: C.white,
        },
        line: {
          color: C.border,
          width: 1,
        },
        rectRadius: 0.05,
      });

      s.addText(f.label, {
        x: xPos + 0.2,
        y: footerY + 0.15,
        w: footW - 0.4,
        h: 0.2,
        fontSize: 8,
        color: f.accent,
        bold: true,
      });

      s.addText(f.val || "Not Uploaded", {
        x: xPos + 0.2,
        y: footerY + 0.4,
        w: footW - 0.4,
        h: 0.45,
        fontSize: 8,
        color: f.val ? C.slateNavy : C.muted,
        fill: {
          color: f.val ? C.skyLight : C.bgLight,
        },
        rectRadius: 0.05,
        line: {
          color: f.val ? C.skyBorder : C.border,
          width: 1,
        },
        valign: "middle",
        margin: 8,
        ...(f.val
          ? {
              hyperlink: {
                url: f.val,
              },
            }
          : {}),
      });
    });
  }

  const fileName = `${campaignTitle.replace(/\s+/g, "_")}_Painting_Report.pptx`;

  await pres.writeFile({
    fileName,
  });
}